import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Monitor, ArrowLeft, Copy, ArrowsClockwise } from "@phosphor-icons/react";

/**
 * v277 — Wall module admin config page.
 *
 * Migrated from « Paramètres → Portail → Mur des urgences public (TV) ».
 * Displays the public token URL to embed on office TVs + the regenerate button.
 * The public URL is now GATED by the module toggle on /modules — if the wall
 * module is disabled, `/api/wall/public` returns 403 even with a valid token.
 */
export default function WallModule() {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [moduleEnabled, setModuleEnabled] = useState(false);

  useEffect(() => {
    api.get("/wall/token").then((r) => setToken(r.data?.token || "")).catch(() => {});
    api.get("/modules")
      .then((r) => {
        const mods = r.data?.modules || r.data || [];
        setModuleEnabled(!!mods.find((m) => m.id === "wall")?.enabled_for_tech);
      })
      .catch(() => {});
  }, []);

  const publicUrl = token ? `${window.location.origin}/mur-public?token=${token}` : "";

  const regenerate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/wall/regenerate-token");
      setToken(r.data?.token || "");
      toast.success("Nouveau token généré — les anciens liens sont désormais invalides");
    } catch { toast.error("Échec de génération"); }
    finally { setBusy(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success("URL copiée dans le presse-papier");
  };

  return (
    <div className="p-6 max-w-3xl" data-testid="wall-module-page">
      <div className="mb-6 flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/modules" data-testid="back-to-modules"><ArrowLeft size={16} className="mr-1"/> Modules</Link>
        </Button>
      </div>
      <header className="mb-6">
        <h1 className="text-3xl font-display font-bold flex items-center gap-2">
          <Monitor size={28}/> Mur des urgences
        </h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Écran mural / TV agence : affiche en temps réel les tickets ouverts triés par priorité (critique + urgente)
          avec code couleur SLA. Activez le module ci-dessous puis générez une URL publique à afficher sur votre TV.
        </p>
      </header>

      <Card className="mb-6">
        <CardHeader><CardTitle className="font-display">État du module</CardTitle></CardHeader>
        <CardContent className="text-sm">
          {moduleEnabled ? (
            <p className="text-emerald-600 dark:text-emerald-400" data-testid="wall-module-active">
              ✓ Module activé — l'URL publique ci-dessous est fonctionnelle. Vous et vos techniciens verrez
              également le lien « Mur des urgences » dans la barre de gauche via la page{" "}
              <Link to="/modules" className="underline">Modules</Link>.
            </p>
          ) : (
            <p className="text-orange-600 dark:text-orange-400" data-testid="wall-module-inactive">
              ⚠ Module désactivé — l'URL publique retourne actuellement une erreur 403.
              Activez le toggle « Accessible aux techniciens » sur la page{" "}
              <Link to="/modules" className="underline">Modules</Link> pour le rendre opérationnel.
            </p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-display">URL publique (TV)</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Générez une URL sans authentification à afficher sur une télé ou un écran mural dans vos locaux.
            Régénérez le token pour invalider immédiatement tous les liens déjà partagés.
          </p>
          {token ? (
            <div className="space-y-3">
              <div className="p-3 rounded-md bg-muted border font-mono text-xs break-all select-all" data-testid="wall-public-url">
                {publicUrl}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button size="sm" variant="secondary" onClick={copy} data-testid="wall-copy-url">
                  <Copy size={14} className="mr-1.5"/> Copier l&apos;URL
                </Button>
                <Button size="sm" variant="secondary" onClick={() => window.open(publicUrl, "_blank")} data-testid="wall-open-url">
                  <Monitor size={14} className="mr-1.5"/> Ouvrir dans un nouvel onglet
                </Button>
                <Button size="sm" variant="ghost" onClick={regenerate} disabled={busy} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid="wall-regenerate">
                  <ArrowsClockwise size={14} className="mr-1.5"/> {busy ? "Génération…" : "Régénérer le token"}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                💡 Sur votre TV : ouvrez cette URL en mode plein écran (F11), la page rafraîchit toutes les 20s automatiquement.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Aucun token public généré — l&apos;URL du mur est actuellement inaccessible sans connexion.</p>
              <Button onClick={regenerate} disabled={busy} data-testid="wall-generate">
                <Monitor size={16} className="mr-2"/> {busy ? "Génération…" : "Générer l'URL publique"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
